import React, {useCallback, useEffect} from 'react';
import {
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import {MdPlayArrow} from 'react-icons/md';
import {ImSpinner2} from 'react-icons/im';
import {realtimeVoices, type RealtimeVoice} from './realtimeVoices';
import {useVoicePreview} from './useVoicePreview';

interface VoiceSettingsModalProps {
    id: string;
    isOpen: boolean;
    isConnected: boolean;
    selectedVoice: RealtimeVoice;
    setSelectedVoice: (voice: RealtimeVoice) => void;
    onClose: () => void;
}

const VoiceSettingsModal: React.FC<VoiceSettingsModalProps> = ({
    id,
    isOpen,
    isConnected,
    selectedVoice,
    setSelectedVoice,
    onClose
}) => {
    const {
        clearPreviewError,
        previewError,
        previewingVoice,
        previewVoice,
        stopVoicePreview
    } = useVoicePreview();

    const close = useCallback(() => {
        stopVoicePreview();
        onClose();
    }, [onClose, stopVoicePreview]);

    useEffect(() => {
        if (isOpen) {
            clearPreviewError();
            return;
        }

        stopVoicePreview();
    }, [clearPreviewError, isOpen, stopVoicePreview]);

    return (
        <Modal isOpen={isOpen} toggle={close} centered>
            <ModalHeader toggle={close}>Voice</ModalHeader>
            <ModalBody>
                {realtimeVoices.map(voice => (
                    <div
                        key={voice.id}
                        className="d-flex align-items-center justify-content-between gap-3 mb-2"
                    >
                        <FormGroup check className="mb-0">
                            <Input
                                id={`${id}-voice-${voice.id}`}
                                name={`${id}-voice`}
                                type="radio"
                                checked={selectedVoice === voice.id}
                                onChange={() => setSelectedVoice(voice.id)}
                            />
                            <Label check for={`${id}-voice-${voice.id}`}>
                                {voice.label}
                                {voice.recommended ? ' (recommended)' : ''}
                            </Label>
                        </FormGroup>
                        <Button
                            color="secondary"
                            outline
                            size="sm"
                            type="button"
                            className="d-inline-flex align-items-center justify-content-center"
                            style={{width: '2rem', height: '2rem'}}
                            disabled={previewingVoice !== null}
                            title={`Preview ${voice.label}`}
                            aria-label={`Preview ${voice.label}`}
                            onClick={() => previewVoice(voice.id)}
                        >
                            {previewingVoice === voice.id ? (
                                <ImSpinner2 className="voice-assistant-control__spinner" />
                            ) : (
                                <MdPlayArrow />
                            )}
                        </Button>
                    </div>
                ))}
                {previewError && (
                    <div className="small text-danger mt-3">{previewError}</div>
                )}
                {isConnected && (
                    <div className="small text-muted mt-3">
                        Voice changes apply the next time you connect.
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={close}>
                    Done
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default VoiceSettingsModal;
